import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort, Sort } from '@angular/material/sort';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, Observable } from 'rxjs';
import { SubSink } from 'subsink';

import { ProductPageResponse } from '@app/api/marketplace/models';
import { ContentStatus, IColumn } from '@products/interfaces/models';
import {
  ProductActions,
  ProductPageRequest,
  ProductSelectors,
} from '@products/store/products.slice';
import { ListTableService } from '@shared/components/list-table/services/list-table.service';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';

import { getTableColumns } from '../../functions/table.functions';

@Component({
  selector: 'app-product-pending',
  templateUrl: './product-rejected.component.html',
  styleUrls: ['./product-rejected.component.scss'],
})
export class ProductRejectedComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  private debounceTime: number = 500;
  private sortParams: Sort = <Sort>{};
  private pageEvent: PageEvent = <PageEvent>{
    pageSize: 0,
  };
  private filter: ProductPageRequest = {
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
    status: ContentStatus.Rejected,
  };

  public isLoading: Observable<boolean> = this.store.select(
    ProductSelectors.selectProductsLoading
  );
  public productTableSource = new MatTableDataSource<ProductPageResponse>();
  public displayedColumns: IColumn[] = [];
  public totalProducts: Observable<number> = this.store.select(
    ProductSelectors.selectTotalProducts
  );
  public pageSizeOptions = paginatorConfiguration.pageSizeOptions;
  public pageSize: number = this.pageSizeOptions[0];
  public contentStatus = ContentStatus;

  constructor(
    private store: Store,
    private translateService: TranslateService,
    private listTableService: ListTableService
  ) {
    this.pageEvent.pageSize = this.pageSize;
  }

  @ViewChild(MatSort) sort = new MatSort();

  ngOnInit(): void {
    this.displayedColumns = getTableColumns();

    this.getCellValue = this.getCellValue.bind(this);
    this.getCellTooltip = this.getCellTooltip.bind(this);
    this.createFilter(this.filter);

    this.subs.sink = this.store
      .select(ProductSelectors.selectProductList)
      .subscribe((products) => {
        if (products) {
          this.productTableSource.data = products;
        }
      });

    this.subs.sink = this.listTableService
      .getFilter()
      .pipe(debounceTime(this.debounceTime))
      .subscribe((filter: ProductPageRequest) => {
        if (filter) {
          this.filter = filter;
          this.createFilter(this.filter);
        }
      });
  }

  private fetchProducts(filter: ProductPageRequest): void {
    this.store.dispatch(ProductActions.loadProducts.trigger(filter));
  }

  private createFilter(filterOptions: ProductPageRequest): void {
    const queryParams: ProductPageRequest = {
      ...(filterOptions ?? {}),
      ...(this.sortParams?.active && this.sortParams?.direction
        ? {
            sort: [`${this.sortParams.active},${this.sortParams.direction}`],
          }
        : {}),
      ean: (filterOptions as any)['eanCodes'],
    } as ProductPageRequest;
    this.fetchProducts(queryParams);
  }

  public handleSortChanged(event: Sort): void {
    this.sortParams = event;
    this.createFilter(this.filter);
  }

  public getCellValue(row: Record<string, string>, field: string): string {
    switch (field) {
      case 'standalonePackingMethodSubType':
        return this.translateService.instant(
          `PRODUCTS.PACKAGING.${row['standalonePackingMethodSubType']}`
        );
      case 'unit':
        return this.translateService.instant(`PRODUCTS.UNIT.${row['unit']}`);
      case 'hasCollectionPackaging':
        return row['collectionPackaging']
          ? `${
              (row['collectionPackaging'] as any).amount
            } ${this.translateService.instant(
              `PACKAGING.PACKAGING.${(
                row['collectionPackaging'] as any
              ).parentPackingMethodSubType.toUpperCase()}`
            )}`
          : '';
      default:
        return row[field];
    }
  }

  public getCellTooltip(
    row: Record<string, string>,
    field: string
  ): string | null {
    switch (field) {
      case 'hasCollectionPackaging':
        return row['collectionPackaging']
          ? `${row['sumOfStandaloneUnits']} ${this.translateService.instant(
              `PACKAGING.PACKAGING.${row[
                'standalonePackingMethodSubType'
              ].toUpperCase()}`
            )}`
          : '';
      default:
        return null;
    }
  }

  ngOnDestroy(): void {
    this.listTableService.resetFilter();
    this.subs.unsubscribe();
  }
}
